import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import AdminLayout from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPricing() {
  const { getAuthHeader } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [prices, setPrices] = useState({});

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/admin/packages`, getAuthHeader());
      setPackages(response.data);
      
      const initialPrices = {};
      response.data.forEach(pkg => {
        initialPrices[pkg.id] = pkg.price;
      });
      setPrices(initialPrices);
    } catch (error) {
      toast.error("Paketler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (id, value) => {
    setPrices(prev => ({ ...prev, [id]: parseFloat(value) || 0 }));
  };

  const handleSave = async (pkg) => {
    setSavingId(pkg.id);
    
    try {
      await axios.put(
        `${API}/admin/packages/${pkg.id}`,
        {
          name: pkg.name,
          description: pkg.description,
          level: pkg.level,
          price: prices[pkg.id],
          duration_days: pkg.duration_days,
          total_sessions: pkg.total_sessions,
          session_duration_minutes: pkg.session_duration_minutes
        },
        getAuthHeader()
      );
      toast.success(`${pkg.name} fiyatı güncellendi`);
      fetchPackages();
    } catch (error) {
      toast.error("Fiyat güncellenemedi");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <AdminLayout title="Ücret">
      <div className="space-y-6">
        <p className="text-neutral-400">Paket fiyatlarını buradan güncelleyebilirsiniz.</p>

        <Card className="border-neutral-800 bg-neutral-900">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800">
                    <TableHead className="text-neutral-400">Paket</TableHead>
                    <TableHead className="text-neutral-400">Seviye</TableHead>
                    <TableHead className="text-neutral-400">Süre</TableHead>
                    <TableHead className="text-neutral-400">Oturum</TableHead>
                    <TableHead className="w-32 text-neutral-400">Fiyat (€)</TableHead>
                    <TableHead className="w-24 text-neutral-400">Durum</TableHead>
                    <TableHead className="w-24 text-neutral-400">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {packages.map((pkg) => (
                    <TableRow key={pkg.id} className="border-neutral-800" data-testid={`pricing-row-${pkg.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-white">{pkg.name}</p>
                          <p className="text-xs text-neutral-500">{pkg.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-red-600/20 text-red-500 border-red-600/30">{pkg.level}</Badge>
                      </TableCell>
                      <TableCell className="text-neutral-300">{pkg.duration_days} gün</TableCell>
                      <TableCell className="text-neutral-300">{pkg.total_sessions} oturum</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={prices[pkg.id] || 0}
                          onChange={(e) => handlePriceChange(pkg.id, e.target.value)}
                          min="0"
                          step="0.01"
                          className="w-24 bg-neutral-800 border-neutral-700 text-white"
                          data-testid={`price-input-${pkg.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge className={pkg.is_active ? "bg-emerald-600/20 text-emerald-500 border-emerald-600/30" : "bg-neutral-700 text-neutral-400"}>
                          {pkg.is_active ? "Aktif" : "Pasif"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                          onClick={() => handleSave(pkg)}
                          disabled={savingId === pkg.id || prices[pkg.id] === pkg.price}
                          data-testid={`save-price-btn-${pkg.id}`}
                        >
                          {savingId === pkg.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
